import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { ref, onValue, query, orderByChild, equalTo } from 'firebase/database';
import HistorialBanner from './HistorialBanner';
import ResultadosMesModal from './ResultadosMesModal';
import './ResultadosMesModal.css';
import { useAuth } from '../auth/AuthContext';
import '../styles/HistorialBanner.css';
import useRadarResults, { radarWeekRanking, santiagoDayRadar } from '../hooks/useRadarResults';

// ──────────────────────────────────────────────────────────────
// DATOS DE PRUEBA mientras no haya signals_history real.
// Se usan SOLO si Firebase está vacío.
// ──────────────────────────────────────────────────────────────
const MOCK_SIGNALS = [
  {
    id: 'mock_1',
    pick_id: 'mock_amager',
    partido: 'Amager @ BMS Herlev',
    liga: 'Basketligaen',
    signal_type: 'UNDER_FUERTE',
    started_at: Date.now() - 15 * 60 * 1000,
    ended_at: Date.now() - 12 * 60 * 1000 - 42 * 1000,
    duration_secs: 138,
    edge_peak: -13.5,
    edge_initial: -8.2,
    period: 3,
    clock: '4:23',
    score_initial: '48-56',
    line: 157.5,
    confianza: 7.6,
  },
  {
    id: 'mock_2',
    pick_id: 'mock_gimle',
    partido: 'Gimle @ Fyllingen',
    liga: 'BLNO',
    signal_type: 'UNDER_FUERTE',
    started_at: Date.now() - 31 * 60 * 1000,
    ended_at: Date.now() - 30 * 60 * 1000 - 13 * 1000,
    duration_secs: 47,
    edge_peak: -9.2,
    edge_initial: -8.1,
    period: 3,
    clock: '8:45',
    score_initial: '54-46',
    line: 162.5,
    confianza: 6.8,
  },
  {
    id: 'mock_3',
    pick_id: 'mock_galata',
    partido: 'Galatasaray @ Merkezefendi',
    liga: 'BSL',
    signal_type: 'OVER_FUERTE',
    started_at: Date.now() - 49 * 60 * 1000,
    ended_at: Date.now() - 46 * 60 * 1000,
    duration_secs: 182,
    edge_peak: 8.4,
    edge_initial: 8.0,
    period: 4,
    clock: '5:12',
    score_initial: '78-82',
    line: 175.5,
    confianza: 7.2,
  },
];

// ── Helpers ───────────────────────────────────────────────────
function formatDuration(secs) {
  if (!secs || secs < 0) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${String(s).padStart(2, '0')}s`;
}

function formatTime(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleTimeString('es-CL', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function formatEdge(edge) {
  if (edge === null || edge === undefined) return '—';
  const val = parseFloat(edge);
  if (isNaN(val)) return '—';
  if (val < 0) return `−${Math.abs(val).toFixed(1)}`;
  return `+${val.toFixed(1)}`;
}

// 2026-05-11: reemplazado isLast24h() por filtro día calendario hora Chile.
// Bug previo: rolling 24h mostraba picks de ayer noche cuando user abría
// el dashboard de madrugada. Vista cliente: queremos solo picks cuya alerta
// TG se mandó HOY (calendario Chile). `started_at` en este schema =
// momento del envío TG (signals_tracker.py:370 lo setea justo antes de
// enviar el DM). Requiere telegram_sent === true para excluir picks que
// nunca llegaron a TG.
//
// Performance Lab + card "📊 Hoy" del Historial usan mismo criterio
// (día calendario Chile + started_at + telegram_sent === true). Vista
// consistente entre lista y card del Historial. Performance Lab idem.
function isInTodayChile(signal, todayWindow) {
  if (signal.telegram_sent !== true) return false;
  if (typeof signal.started_at !== 'number') return false;
  return signal.started_at >= todayWindow.startMs && signal.started_at < todayWindow.endMs;
}

// ── Helpers ventana día Chile (DST-safe) — mismos que PerformanceLab ──
// Documentado en DECISIONES.md como source-of-truth canónico para stats.
function santiagoDateStrFromDate(date) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Santiago',
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(date);
}

function santiagoMidnightToUTCmsHelper(yyyyMmDd) {
  const y = parseInt(yyyyMmDd.slice(0, 4), 10);
  const m = parseInt(yyyyMmDd.slice(5, 7), 10) - 1;
  const d = parseInt(yyyyMmDd.slice(8, 10), 10);
  for (const utcHour of [3, 4]) {
    const candidate = Date.UTC(y, m, d, utcHour, 0, 0);
    const hhmm = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Santiago',
      hour12: false, hour: '2-digit', minute: '2-digit',
    }).format(new Date(candidate));
    if (hhmm === '00:00' || hhmm === '24:00') return candidate;
  }
  return Date.UTC(y, m, d, 4, 0, 0);
}

function getTodayChileWindow(now) {
  const todayStr = santiagoDateStrFromDate(now);
  const tomorrowAnchor = new Date(new Date(`${todayStr}T12:00:00Z`).getTime() + 86400000);
  const tomorrowStr = santiagoDateStrFromDate(tomorrowAnchor);
  return {
    startMs: santiagoMidnightToUTCmsHelper(todayStr),
    endMs: santiagoMidnightToUTCmsHelper(tomorrowStr),
    todayStr,
  };
}

function fmtDDMM(yyyyMmDd) {
  return `${yyyyMmDd.slice(8, 10)}/${yyyyMmDd.slice(5, 7)}`;
}

function fmtRelative(ts, now) {
  if (!ts) return '—';
  const diffMs = now - ts;
  if (diffMs < 0) return 'ahora';
  if (diffMs < 60_000) return `hace ${Math.floor(diffMs / 1000)}s`;
  if (diffMs < 3_600_000) return `hace ${Math.floor(diffMs / 60_000)} min`;
  return new Intl.DateTimeFormat('es-CL', {
    timeZone: 'America/Santiago', hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(new Date(ts));
}

export default function SignalsHistory() {
  // 2026-05-17: SHADOW MODE — picks con shadow_only===true son emitidos por
  // el motor V6 en modo pausa-controlada. Cliente NO debe verlos. Admin SÍ
  // (con badge 🔬 SHADOW), pero NUNCA cuentan en stats agregadas.
  const { isAdmin } = useAuth();

  // 2026-05-11: `signals` ahora es useMemo derivado de allSignals + now +
  // ventana día Chile. Antes era state seteado en el callback con filter
  // estático. Refactor permite que la ventana día Chile se recompute cada
  // 10s (auto-tick) — al pasar medianoche, picks de ayer salen de la lista
  // sin requerir refresh manual del browser.
  const [allSignals, setAllSignals] = useState([]); // raw RTDB sin filtro, fuente única
  const [loading, setLoading]     = useState(true);
  const [usingMock, setUsingMock] = useState(false);
  const [filter, setFilter]       = useState('all');
  const [now, setNow]             = useState(Date.now());
  const [livePicksById, setLivePicksById] = useState({});
  const [modalAbierto, setModalAbierto] = useState(false);
  // Timestamp del último snapshot RTDB recibido (para "Última actualización")
  const [lastDataTs, setLastDataTs] = useState(null);
  // Pestaña del historial: 'live' (motor, intacto) | 'radar' (Estrategia 1,
  // fuente /radar_vip_results — nunca se suman ambos en un mismo número).
  const [histTab, setHistTab] = useState('live');

  // Lista visible derivada — filtro día calendario hora Chile + telegram_sent.
  // Recomputa con `now` (auto-tick 10s) para que al pasar medianoche los
  // picks de "ayer" desaparezcan automáticamente sin requerir refresh.
  // 2026-05-11 hotfix: este useMemo debe DECLARARSE antes de cualquier
  // useEffect que use `signals` en sus deps — sino TDZ ReferenceError
  // rompe el render entero (pantalla en blanco).
  const signals = useMemo(() => {
    const window_ = getTodayChileWindow(new Date(now));
    if (usingMock) {
      // Modo "datos de prueba" — no exigimos telegram_sent (mocks no lo tienen),
      // pero sí día Chile para que el filtro temporal sea consistente.
      return [...MOCK_SIGNALS]
        .filter(s => typeof s.started_at === 'number'
          && s.started_at >= window_.startMs
          && s.started_at < window_.endMs)
        .sort((a, b) => (b.started_at || 0) - (a.started_at || 0));
    }
    return allSignals
      .filter(s => {
        if (!isInTodayChile(s, window_)) return false;
        // SHADOW MODE (2026-05-17): cliente NO ve shadow; admin sí (con badge).
        if (s.shadow_only === true && !isAdmin) return false;
        return true;
      })
      .sort((a, b) => (b.started_at || 0) - (a.started_at || 0));
  }, [allSignals, now, usingMock, isAdmin]);

  // Timer que actualiza el contador "activa hace Xm Ys" cada 1 segundo.
  // Solo se ejecuta si hay alguna señal activa (sin ended_at) para no
  // gastar CPU innecesariamente cuando todas estan cerradas.
  useEffect(() => {
    const hasActive = signals.some(s => !s.ended_at);
    if (!hasActive) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [signals]);

  // Listener de picks en vivo — para obtener la línea actual y mostrar
  // el movimiento de la línea en señales ACTIVAS del historial
  useEffect(() => {
    const picksRef = ref(db, 'picks');
    const unsub = onValue(picksRef, snap => {
      const data = snap.val() || {};
      // Indexar por key del path (ej: "aps_487910") para lookup rápido
      // Los picks en Firebase están indexados por su ID como clave directa.
      const byId = {};
      Object.entries(data).forEach(([key, pick]) => {
        if (pick && typeof pick === 'object') {
          byId[key] = pick;
          // También indexar por el campo id interno si existe (fallback)
          if (pick.id && pick.id !== key) {
            byId[pick.id] = pick;
          }
        }
      });
      setLivePicksById(byId);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const signalsRef = query(ref(db, 'signals_history'), orderByChild('telegram_sent'), equalTo(true));
    const unsub = onValue(
      signalsRef,
      snapshot => {
        const data = snapshot.val();
        if (!data || Object.keys(data).length === 0) {
          setAllSignals([]);
          setUsingMock(true);
        } else {
          // 2026-05-11: NO filtramos aquí — el filtro día Chile + telegram_sent
          // se aplica en el useMemo `signals` derivado, que se recompute con
          // `now` (auto-tick 10s) para manejar el cruce de medianoche.
          const fullList = Object.entries(data).map(([id, val]) => ({ id, ...val }));
          setAllSignals(fullList);
          setUsingMock(false);
        }
        setLastDataTs(Date.now());
        setLoading(false);
      },
      err => {
        console.error('[SignalsHistory] Firebase error:', err);
        setAllSignals([]);
        setUsingMock(true);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  // Auto-tick cada 10s para refrescar "hace N min" + recomputar ventana día Chile
  // (importante en transición de medianoche y para responsividad visual).
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 10_000);
    return () => clearInterval(id);
  }, []);

  // Stats "Hoy hora Chile" alineadas con sendDailyReport / PerformanceLab.
  // Regla canónica DECISIONES.md: día calendario Chile + started_at +
  // telegram_sent === true + result IN (WIN, LOSS).
  const todayChile = (() => {
    const w = getTodayChileWindow(new Date(now));
    let wins = 0, losses = 0, pending = 0, totalEmitted = 0;
    for (const s of allSignals) {
      if (s.telegram_sent !== true) continue;
      // SHADOW MODE (2026-05-17): nunca contar shadow_only en stats — ni
      // cliente ni admin (los shadow son "lo que el motor hubiera emitido",
      // no parte del track-record real visible al VIP).
      if (s.shadow_only === true) continue;
      if (typeof s.started_at !== 'number') continue;
      if (s.started_at < w.startMs || s.started_at >= w.endMs) continue;
      totalEmitted++;
      // CASHOUT_WIN/LOSS (2026-05-17): cuentan como WIN/LOSS en el WR del día
      // para que el track-record refleje el yield real (cashout parcial sigue
      // siendo decisión exitosa o fallida según signo del yield).
      if (s.result === 'WIN' || s.result === 'CASHOUT_WIN') wins++;
      else if (s.result === 'LOSS' || s.result === 'CASHOUT_LOSS') losses++;
      else if (s.result === 'PUSH') { /* descartado del denom */ }
      else pending++;
    }
    const decided = wins + losses;
    const wr = decided > 0 ? (wins / decided) * 100 : null;
    return { wins, losses, pending, decided, wr, ddmm: fmtDDMM(w.todayStr), totalEmitted };
  })();

  const filtered = signals.filter(s => {
    if (filter === 'all') return true;
    if (filter === 'under')  return s.signal_type === 'UNDER_FUERTE';
    if (filter === 'over')   return s.signal_type === 'OVER_FUERTE';
    if (filter === 'active') return !s.ended_at;
    return true;
  });

  // KPIs (solo los usados por las tabs de filtro)
  const underCount = signals.filter(s => s.signal_type === 'UNDER_FUERTE').length;
  const overCount  = signals.filter(s => s.signal_type === 'OVER_FUERTE').length;

  if (loading) {
    return (
      <div className="page" style={{ padding: 20 }}>
        <div style={{ color: 'var(--muted)', textAlign: 'center', padding: 48 }}>
          Cargando historial…
        </div>
      </div>
    );
  }

  return (
    <div className="page" style={{ padding: '24px 28px' }}>

      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h1 className="page-title" style={{ margin: 0 }}>
          Historial de señales
        </h1>
        <div className="page-sub" style={{ marginTop: 4, fontSize: 13, color: 'var(--muted)' }}>
          últimas 24 horas
          {usingMock && (
            <span style={{
              marginLeft: 10,
              fontSize: 10,
              padding: '2px 8px',
              background: 'var(--warn-soft)',
              color: 'var(--warn)',
              borderRadius: 999,
              fontWeight: 700,
              letterSpacing: '0.03em',
            }}>
              datos de prueba
            </span>
          )}
        </div>
      </div>

      {/* Pestañas LIVE · Motor | RADAR · Estrategia 1 (canales paralelos) */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <FilterTab active={histTab === 'live'} onClick={() => setHistTab('live')} label="LIVE · Motor" />
        <FilterTab active={histTab === 'radar'} onClick={() => setHistTab('radar')} label="📡 RADAR · Estrategia 1" />
      </div>

      {histTab === 'radar' && <RadarHistoryTab />}

      {histTab === 'live' && <>
      {/* Stats del día hora Chile — alineado con sendDailyReport / PerformanceLab.
          Regla DECISIONES.md: día calendario Chile + started_at + telegram_sent +
          result IN (WIN, LOSS). PUSH descarta del denominador WR. */}
      {!usingMock && todayChile.totalEmitted > 0 && (
        <div style={{
          background: 'var(--bg-elev)',
          border: '0.5px solid var(--border)',
          borderRadius: 12,
          padding: '14px 16px',
          marginBottom: 16,
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: 10,
            gap: 8,
            flexWrap: 'wrap',
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>
              📊 Hoy {todayChile.ddmm} hora Chile
            </div>
            {/* Indicador live: dot verde pulsante + label "Actualizado hace X".
                El dot confirma visualmente que el listener real-time está vivo
                aunque los stats no cambien (por falta de signals nuevos en RTDB).
                Reusa la animación `live-mini-blink` definida en index.css. */}
            <div style={{
              fontSize: 11,
              color: 'var(--muted-2, var(--muted))',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}>
              <span
                className="live-mini-dot"
                style={{ background: 'var(--pos)', color: 'var(--pos)' }}
                aria-hidden="true"
              />
              <span>Actualizado {fmtRelative(lastDataTs, now)}</span>
            </div>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: 10,
          }}>
            <MiniKpi
              label="Picks resueltos"
              value={todayChile.decided}
              sub={todayChile.pending > 0 ? `+ ${todayChile.pending} en curso` : null}
              tone="neutral"
            />
            <MiniKpi label="Ganadas" value={todayChile.wins} tone="win" />
            <MiniKpi label="Perdidas" value={todayChile.losses} tone="loss" />
            <MiniKpi
              label="Win rate"
              value={todayChile.wr == null ? '—' : `${todayChile.wr.toFixed(1)}%`}
              tone={todayChile.wr == null ? 'neutral' : (todayChile.wr >= 50 ? 'win' : 'loss')}
              isText
            />
          </div>
        </div>
      )}

      {/* 2026-05-10: cards "Total / UNDER★ / OVER★ / Duración prom" eliminadas.
          Duplican info que ya está en la card "📊 Hoy" arriba y en las tabs
          de filtro de abajo (los contadores entre paréntesis). Cliente reportó
          confusión visual. */}

      <HistorialBanner />

      <button
        onClick={() => setModalAbierto(true)}
        className="ee-historial-mes-button"
        aria-label="Ver historial completo de un mes"
      >
        <div className="ee-historial-mes-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2"
               strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
        </div>
        <div className="ee-historial-mes-text">
          <p className="ee-historial-mes-title">Ver historial completo de un mes</p>
          <p className="ee-historial-mes-subtitle">31 días reales con tu banca</p>
        </div>
        <svg className="ee-historial-mes-arrow" width="18" height="18"
             viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
             strokeLinecap="round" strokeLinejoin="round">
          <line x1="5" y1="12" x2="19" y2="12"></line>
          <polyline points="12 5 19 12 12 19"></polyline>
        </svg>
      </button>

      <ResultadosMesModal
        open={modalAbierto}
        onClose={() => setModalAbierto(false)}
      />

      {/* Tabs filtro — scroll horizontal en mobile si no caben */}
      <div style={{
        display: 'flex',
        gap: 8,
        marginBottom: 14,
        overflowX: 'auto',
        paddingBottom: 4,
        WebkitOverflowScrolling: 'touch',
      }}>
        <FilterTab active={filter === 'all'}    onClick={() => setFilter('all')}    label={`Todas (${signals.length})`} />
        <FilterTab active={filter === 'under'}  onClick={() => setFilter('under')}  label={`UNDER ★ (${underCount})`} />
        <FilterTab active={filter === 'over'}   onClick={() => setFilter('over')}   label={`OVER ★ (${overCount})`} />
        <FilterTab active={filter === 'active'} onClick={() => setFilter('active')} label="Activas" />
      </div>

      {/* Cards de señales — stack vertical mobile-first */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}>
        {filtered.length === 0 ? (
          <div style={{
            padding: 40,
            textAlign: 'center',
            fontSize: 13,
            color: 'var(--muted)',
            background: 'var(--bg-elev)',
            border: '0.5px solid var(--border)',
            borderRadius: 12,
          }}>
            {filter === 'all' ? (
              <>
                Sin señales registradas en las últimas 24 horas.
                <div style={{ fontSize: 11, marginTop: 8, color: 'var(--muted-2)' }}>
                  Las señales UNDER ★ y OVER ★ aparecerán acá automáticamente cuando el motor las detecte.
                </div>
              </>
            ) : (
              `Sin señales en filtro "${filter}".`
            )}
          </div>
        ) : (
          filtered.map(s => s.is_manual === true
            ? <ManualSignalCard key={s.id} signal={s} now={now} />
            : <SignalCard key={s.id} signal={s} now={now} livePicksById={livePicksById} />
          )
        )}
      </div>
      </>}
    </div>
  );
}

// ── Pestaña RADAR · Estrategia 1 ─────────────────────────────
// Fuente /radar_vip_results vía useRadarResults. Reglas (Matías 2026-09-03):
// separada de LIVE (nunca sumar), SIN categoría/colores de liga, SIN yield
// ni unidades — solo acierto %, W-L y conteos. Cliente y admin ven lo mismo.
const MESES_RADAR = ['ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO',
  'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'];

function RadarHistoryTab() {
  const { alerts, loading } = useRadarResults();
  const [period, setPeriod] = useState('mes');     // mes | prev | all
  const [weekOffset, setWeekOffset] = useState(0); // 0 = en curso, -1 = anterior

  const hoy = santiagoDayRadar(new Date());        // YYYY-MM-DD Chile
  const [y, m] = hoy.split('-').map(Number);
  const mesActual = hoy.slice(0, 7);
  const mesPrev = `${m === 1 ? y - 1 : y}-${String(m === 1 ? 12 : m - 1).padStart(2, '0')}`;

  const subset = useMemo(() => {
    if (period === 'all') return alerts;
    const pref = period === 'mes' ? mesActual : mesPrev;
    return alerts.filter(a => a.fecha.startsWith(pref));
  }, [alerts, period, mesActual, mesPrev]);

  const ranking = useMemo(() => radarWeekRanking(alerts, weekOffset), [alerts, weekOffset]);

  // agrupar por día Chile desc
  const grupos = useMemo(() => {
    const map = new Map();
    for (const a of subset) {
      if (!map.has(a.fecha)) map.set(a.fecha, []);
      map.get(a.fecha).push(a);
    }
    return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [subset]);

  const fmtDia = (d) => {
    const [, mm, dd] = d.split('-');
    return `${parseInt(dd, 10)} DE ${MESES_RADAR[parseInt(mm, 10) - 1]}`;
  };
  const box = {
    background: 'var(--bg-elev)', border: '0.5px solid var(--border)',
    borderRadius: 12, padding: '14px 16px', marginBottom: 16,
  };

  if (loading) {
    return <div style={{ ...box, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>Cargando resultados RADAR…</div>;
  }

  return (
    <div>
      {/* Cabecera: acierto / W-L / alertas del período */}
      <div style={box}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>
            📡 RADAR · Estrategia 1
            <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--muted)' }}>
              {period === 'all' ? 'histórico completo' : period === 'mes' ? MESES_RADAR[m - 1].toLowerCase() : 'mes anterior'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {[['mes', 'Mes actual'], ['prev', 'Mes anterior'], ['all', 'Todo']].map(([k, lbl]) => (
              <FilterTab key={k} active={period === k} onClick={() => setPeriod(k)} label={lbl} />
            ))}
          </div>
        </div>
        {/* Cabecera minimal (Matías 2026-09-03): SOLO total de alertas del
            período — sin acierto %, sin W-L. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(110px, 220px)', gap: 10 }}>
          <MiniKpi label="Alertas" value={subset.length} tone="neutral" />
        </div>
      </div>

      {/* Ranking semanal de ligas (lun–dom Chile, mín. 3 resueltas, top 8) */}
      <div style={box}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>
          🏆 Ligas con mejores resultados {weekOffset === 0 ? 'esta semana' : 'la semana anterior'}
        </div>
        {ranking.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>
            Aún no hay ligas con 3+ alertas resueltas {weekOffset === 0 ? 'esta semana' : 'esa semana'}.
          </div>
        ) : (
          /* SOLO nombres de liga (Matías 2026-09-03) — el orden interno sí
             es por aciertos de la semana, pero no se muestran números. */
          <div>
            {ranking.map((r) => (
              <div key={r.liga} style={{ fontSize: 13, fontWeight: 600, padding: '6px 0', borderTop: '0.5px solid var(--border)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.liga}
              </div>
            ))}
          </div>
        )}
        <button
          onClick={() => setWeekOffset(weekOffset === 0 ? -1 : 0)}
          style={{ marginTop: 10, background: 'none', border: 'none', color: 'var(--accent, var(--pos))', fontSize: 12, cursor: 'pointer', padding: 0 }}
        >
          {weekOffset === 0 ? 'Ver semana anterior →' : '← Volver a esta semana'}
        </button>
      </div>

      {/* Lista por día */}
      {grupos.length === 0 ? (
        <div style={{ ...box, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
          Sin alertas RADAR en este período.
        </div>
      ) : grupos.map(([dia, items]) => (
        <div key={dia} style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', color: 'var(--muted)', margin: '0 0 6px 2px' }}>
            {fmtDia(dia)}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {items.map((a) => (
              <div key={a.id} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                background: 'var(--bg-elev)', border: '0.5px solid var(--border)',
                borderRadius: 10, padding: '10px 12px',
              }}>
                <div style={{
                  width: 26, height: 26, borderRadius: 8, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 800,
                  background: !a.resolved ? 'var(--bg)' : a.won ? 'var(--pos-soft)' : 'var(--neg-soft)',
                  color: !a.resolved ? 'var(--muted)' : a.won ? 'var(--pos)' : 'var(--neg)',
                }}>
                  {!a.resolved ? '·' : a.won ? 'W' : 'L'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {a.partido}
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>
                    {a.side} {a.linea ?? '—'} · {a.ventana} · {a.liga}
                    {!a.resolved && <span style={{ marginLeft: 6, fontStyle: 'italic' }}>En curso</span>}
                  </div>
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                  {a.edge == null ? '—' : (a.edge > 0 ? `+${a.edge.toFixed(1)}` : `−${Math.abs(a.edge).toFixed(1)}`)}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Componentes auxiliares ───────────────────────────────────

function MiniKpi({ label, value, sub, tone, isText }) {
  const tones = {
    neutral: { bg: 'var(--bg-elev)',  fg: 'var(--ink)', subColor: 'var(--muted)', border: '0.5px solid var(--border)' },
    win:     { bg: 'var(--pos-soft)', fg: 'var(--pos)', subColor: 'var(--pos)',   border: 'none' },
    loss:    { bg: 'var(--neg-soft)', fg: 'var(--neg)', subColor: 'var(--neg)',   border: 'none' },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <div style={{
      background: t.bg,
      padding: '10px 12px',
      borderRadius: 8,
      border: t.border,
    }}>
      <div style={{
        fontSize: 10,
        color: t.subColor,
        marginBottom: 4,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}>{label}</div>
      <div style={{
        fontSize: isText ? 18 : 22,
        fontWeight: 600,
        color: t.fg,
        lineHeight: 1.1,
      }}>{value}</div>
      {sub && (
        <div style={{ fontSize: 10, color: t.subColor, marginTop: 4, opacity: 0.8 }}>{sub}</div>
      )}
    </div>
  );
}

// KpiCard removido (2026-05-10): las 4 cards Total/UNDER★/OVER★/Duración
// se eliminaron porque duplicaban info ya visible en "📊 Hoy" y tabs.

function FilterTab({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: active ? 'var(--ink)' : 'transparent',
        color: active ? 'var(--bg-elev)' : 'var(--ink-2)',
        border: '0.5px solid ' + (active ? 'var(--ink)' : 'var(--border)'),
        padding: '6px 14px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 500,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      {label}
    </button>
  );
}

// CARD mobile-first: borde lateral color + grid 4 datos
function SignalCard({ signal, now, livePicksById = {} }) {
  const isUnder  = signal.signal_type === 'UNDER_FUERTE';
  const isActive = !signal.ended_at;
  const result   = signal.result; // "WIN" | "LOSS" | "PUSH" | null

  // Estados: GANADA (verde destacada) / PERDIDA (discreta) / ACTIVA (normal) / CERRADA SIN RESULTADO
  const isWon    = result === 'WIN';
  const isLost   = result === 'LOSS';
  const isPush   = result === 'PUSH';
  const hasResult = isWon || isLost || isPush;

  // Colores según estado
  const edgeColor = signal.edge_peak < 0 ? 'var(--neg)' : 'var(--warn)';

  // Tiempo transcurrido
  const elapsedSecs = isActive && signal.started_at
    ? Math.floor(((now || Date.now()) - signal.started_at) / 1000)
    : (signal.duration_secs || 0);
  const durationLabel = isActive
    ? `activa · ${formatDuration(elapsedSecs)}`
    : formatDuration(elapsedSecs);

  // Total real y diferencia
  const totalFinal = signal.total_final;
  const diferencia = signal.diferencia;

  // ── LÍNEA EN VIVO (solo para señales activas) ──────
  // Buscar la línea actual del pick en /picks para mostrar el movimiento
  // signal.line es la línea del momento de la señal, live line puede haber cambiado
  const linePick = isActive && signal.pick_id ? livePicksById[signal.pick_id] : null;
  const lineLive = linePick
    ? parseFloat(linePick.linea || linePick.line || linePick.over_under || 0) || null
    : null;
  const lineSignal = parseFloat(signal.line || 0) || null;
  const lineMoved = isActive && lineLive && lineSignal && Math.abs(lineLive - lineSignal) >= 0.5;
  const lineDelta = lineMoved ? (lineLive - lineSignal) : 0;

  // ── ESTILO SEGÚN ESTADO ────────────────────────────
  let cardStyle = {
    background: 'var(--bg-elev)',
    border: '0.5px solid var(--border)',
    borderLeft: `3px solid ${isUnder ? 'var(--pos)' : 'var(--warn)'}`,
    borderRadius: 10,
    padding: '12px 14px',
    transition: 'all 0.2s',
  };
  let partidoColor  = 'var(--ink)';
  let metaColor     = 'var(--muted-2)';
  let cardOpacity   = 1;
  let tagBg         = isUnder ? 'var(--pos-soft)' : 'var(--warn-soft)';
  let tagFg         = isUnder ? 'var(--pos)'      : 'var(--warn)';

  if (isWon) {
    // GANADA: verde intenso, dominante
    cardStyle = {
      background: '#9FE1CB',
      border: '0.5px solid #1D9E75',
      borderLeft: '6px solid #0F6E56',
      borderRadius: 10,
      padding: '14px 16px',
      boxShadow: '0 2px 8px rgba(29,158,117,0.15)',
      transition: 'all 0.2s',
    };
    partidoColor = '#04342C';
    metaColor    = '#0F6E56';
    tagBg = 'white';
    tagFg = '#04342C';
  } else if (isLost) {
    // PERDIDA: casi invisible, neutra
    cardOpacity = 0.55;
    cardStyle.borderLeft = '3px solid var(--border)';
    partidoColor = 'var(--muted)';
    metaColor    = 'var(--muted-2)';
    tagBg        = 'var(--bg-subtle)';
    tagFg        = 'var(--muted)';
  } else if (isPush) {
    // PUSH: neutro discreto
    cardOpacity = 0.75;
    tagBg = 'var(--bg-subtle)';
    tagFg = 'var(--muted)';
  }

  return (
    <div style={{ ...cardStyle, opacity: cardOpacity }}>
      {/* Fila 1: Partido + chips de estado */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 10,
        marginBottom: 8,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 14,
            fontWeight: 600,
            color: partidoColor,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {signal.partido}
          </div>
          <div style={{ fontSize: 11, color: metaColor, marginTop: 2, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span>{signal.liga}</span>
            {signal.shadow_only === true && (
              <span
                title="SHADOW: motor V6 evaluó este pick pero NO se envió a Telegram (pausa técnica). Visible solo para admin."
                style={{
                  background: '#6B5BFF',
                  color: 'white',
                  fontSize: 9.5,
                  fontWeight: 700,
                  padding: '1px 6px',
                  borderRadius: 4,
                  letterSpacing: 0.3,
                  whiteSpace: 'nowrap',
                }}
              >
                🔬 SHADOW
              </span>
            )}
            {signal.source === 'over_fuerte' && (
              <span style={{
                background: '#FF6B2C',
                color: 'white',
                fontSize: 9.5,
                fontWeight: 700,
                padding: '1px 6px',
                borderRadius: 4,
                letterSpacing: 0.3,
                whiteSpace: 'nowrap',
              }}>
                🔥 OVER
              </span>
            )}
            <span>
              {signal.period ? ` · Q${signal.period}` : ''}
              {signal.clock ? ` ${signal.clock}` : ''}
              {hasResult && totalFinal != null ? ` · FIN · ${totalFinal} pts` : ''}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
          {/* Chip de resultado: solo GANÓ (verde destacado) o PUSH */}
          {isWon && (
            <span style={{
              background: '#1D9E75',
              color: 'white',
              fontSize: 11,
              fontWeight: 700,
              padding: '4px 11px',
              borderRadius: 999,
              whiteSpace: 'nowrap',
              letterSpacing: '0.04em',
            }}>
              GANÓ ✓
            </span>
          )}
          {isPush && (
            <span style={{
              background: 'var(--bg-subtle)',
              color: 'var(--ink-2)',
              fontSize: 10,
              fontWeight: 600,
              padding: '3px 9px',
              borderRadius: 999,
              whiteSpace: 'nowrap',
            }}>
              PUSH
            </span>
          )}
          {/* (no chip para LOSS — queremos minimizar impacto negativo) */}

          {isActive && (
            <span style={{
              background: '#1D9E75',
              color: 'white',
              fontSize: 10,
              fontWeight: 600,
              padding: '3px 9px',
              borderRadius: 999,
              whiteSpace: 'nowrap',
            }}>
              ACTIVA
            </span>
          )}
          {/* Chip Telegram — solo si tiene info del flag */}
          {signal.telegram_sent === true && (
            <span
              title="Enviada a Telegram"
              style={{
                background: 'var(--info-soft)',
                color: 'var(--info)',
                fontSize: 11,
                fontWeight: 600,
                padding: '3px 9px',
                borderRadius: 999,
                whiteSpace: 'nowrap',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              📨 TG
            </span>
          )}
          {signal.telegram_sent === false && signal.telegram_window === false && (
            <span
              title={signal.telegram_skip_reason || 'Fuera de ventana Telegram'}
              style={{
                background: 'var(--bg-subtle)',
                color: 'var(--muted)',
                fontSize: 11,
                fontWeight: 500,
                padding: '3px 9px',
                borderRadius: 999,
                whiteSpace: 'nowrap',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              🔕 Sin TG
            </span>
          )}
          {/* Chip del tipo UNDER/OVER */}
          <span style={{
            background: tagBg,
            color: tagFg,
            fontSize: 11,
            fontWeight: 600,
            padding: '3px 9px',
            borderRadius: 999,
            whiteSpace: 'nowrap',
            border: isWon ? '0.5px solid #5DCAA5' : 'none',
          }}>
            {isUnder ? 'UNDER ★' : 'OVER ★'}
          </span>
        </div>
      </div>

      {/* Fila 2: Grid de datos */}
      <div
        className="signals-card-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: isActive && !hasResult
            ? 'repeat(auto-fit, minmax(90px, 1fr))'   // activa: LÍNEA ALERTA + LÍNEA VIVA + EDGE + INICIO + DURACIÓN
            : 'repeat(4, 1fr)',  // cerrada: LÍNEA + TOTAL REAL + DIFERENCIA + DURACIÓN
          gap: 6,
          paddingTop: 8,
          borderTop: `0.5px solid ${isWon ? '#9FE1CB' : 'var(--border)'}`,
        }}
      >
        {isActive ? (
          <>
            <DataBlock
              label="LÍNEA ALERTA"
              value={signal.line || '—'}
              mono
              bold
            />
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontSize: 9,
                color: 'var(--muted)',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                marginBottom: 2,
              }}>
                LÍNEA VIVA
              </div>
              <div style={{
                fontSize: 13,
                fontWeight: 700,
                fontFamily: 'ui-monospace, monospace',
                display: 'flex',
                alignItems: 'baseline',
                gap: 5,
                flexWrap: 'wrap',
                color: lineMoved
                  ? (lineDelta > 0 ? 'var(--pos)' : 'var(--neg)')
                  : 'var(--ink)',
              }}>
                <span>{lineLive != null ? lineLive : '—'}</span>
                {lineMoved && (
                  <span style={{
                    fontSize: 10,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                  }}>
                    {lineDelta > 0 ? '↑' : '↓'}{Math.abs(lineDelta).toFixed(1)}
                  </span>
                )}
              </div>
            </div>
          </>
        ) : (
          <DataBlock
            label="LÍNEA"
            value={signal.line || '—'}
            color={isWon ? '#04342C' : undefined}
            mono
            bold
          />
        )}

        {/* Si tiene resultado: mostrar TOTAL REAL y DIFERENCIA.
            Si no: mostrar EDGE PICO (como antes) */}
        {hasResult ? (
          <>
            <DataBlock
              label="TOTAL REAL"
              value={totalFinal != null ? String(totalFinal) : '—'}
              color={isWon ? '#0F6E56' : (isLost ? 'var(--muted)' : 'var(--ink-2)')}
              mono
              bold={isWon}
            />
            <DataBlock
              label="DIFERENCIA"
              value={diferencia != null
                ? `${diferencia >= 0 ? '+' : ''}${diferencia.toFixed(1)}`
                : '—'}
              color={isWon ? '#0F6E56' : (isLost ? 'var(--muted)' : 'var(--ink-2)')}
              bold={isWon}
            />
          </>
        ) : (
          <>
            <DataBlock
              label="EDGE PICO"
              value={formatEdge(signal.edge_peak)}
              color={edgeColor}
              bold
            />
            <DataBlock label="INICIO" value={formatTime(signal.started_at)} mono />
          </>
        )}

        <DataBlock
          label="DURACIÓN"
          value={durationLabel}
          color={isActive ? 'var(--pos)' : (isWon ? '#04342C' : 'var(--ink-2)')}
          bold={isActive || isWon}
        />
      </div>
    </div>
  );
}

// ── ManualSignalCard — render para picks manuales multi-deporte ─────
// Usado cuando signal.is_manual === true. Layout simple:
//   [icono deporte] [partido]  ·  badge "📍 PICK MANUAL"  ·  chip resultado
//   liga · mercado (texto libre)
//   [cuota] [stake] [yield] [hora]
//
// NO comparte estilos con SignalCard (motor) para evitar acoplamientos.
// Si is_manual === false (motor V6) se sigue usando SignalCard.

const DEPORTE_ICONS = {
  basquet: '🏀',
  tenis: '🎾',
  futbol: '⚽',
  voley: '🏐',
  hockey: '🏒',
  otro: '🎯',
};

function ManualSignalCard({ signal, now }) {
  const result = signal.result; // WIN | LOSS | PUSH | CASHOUT_WIN | CASHOUT_LOSS | null
  // 2026-05-17: CASHOUT_WIN/CASHOUT_LOSS son resultados de cierre por cashout.
  //   - CASHOUT_WIN se renderiza igual que WIN (verde) + icono 💰 en el chip
  //   - CASHOUT_LOSS se renderiza igual que LOSS (atenuado) + icono 💸 en chip
  // 2026-05-18: render distinto por tipo_pick (simple | combinada | betbuilder).
  //   - simple:     header partido único + mercado en chip
  //   - combinada:  header "COMBINADA" + lista de selecciones (icono por deporte)
  //   - betbuilder: header "BET BUILDER" + partido único + lista de mercados
  const isWon = result === 'WIN' || result === 'CASHOUT_WIN';
  const isLost = result === 'LOSS' || result === 'CASHOUT_LOSS';
  const isPush = result === 'PUSH';
  const isCashoutWin = result === 'CASHOUT_WIN';
  const isCashoutLoss = result === 'CASHOUT_LOSS';
  const isPending = !isWon && !isLost && !isPush;
  const icon = DEPORTE_ICONS[(signal.deporte || 'otro').toLowerCase()] || DEPORTE_ICONS.otro;

  const tipoPick = (signal.tipo_pick || 'simple').toLowerCase();
  const isCombinada = tipoPick === 'combinada';
  const isBetBuilder = tipoPick === 'betbuilder';
  const selecciones = Array.isArray(signal.selecciones) ? signal.selecciones : [];

  // Estilos según estado (mismo lenguaje visual que SignalCard motor)
  let cardStyle = {
    background: 'var(--bg-elev)',
    border: '0.5px solid var(--border)',
    borderLeft: '3px solid #FF6B2C',  // naranja distintivo manual
    borderRadius: 10,
    padding: '12px 14px',
    transition: 'all 0.2s',
  };
  let partidoColor = 'var(--ink)';
  let metaColor = 'var(--muted-2)';
  let cardOpacity = 1;

  if (isWon) {
    cardStyle = {
      background: '#9FE1CB',
      border: '0.5px solid #1D9E75',
      borderLeft: '6px solid #0F6E56',
      borderRadius: 10,
      padding: '14px 16px',
      boxShadow: '0 2px 8px rgba(29,158,117,0.15)',
      transition: 'all 0.2s',
    };
    partidoColor = '#04342C';
    metaColor = '#0F6E56';
  } else if (isLost) {
    cardOpacity = 0.55;
    cardStyle.borderLeft = '3px solid var(--border)';
    partidoColor = 'var(--muted)';
    metaColor = 'var(--muted-2)';
  } else if (isPush) {
    cardOpacity = 0.75;
  }

  const yu = signal.yield_u;
  const odds = signal.odds;
  const stake = signal.stake_u_publicado;
  const startedAt = signal.started_at;

  // Header dinámico por tipo
  let headerIcon = icon;
  let headerTitle;
  if (isCombinada) {
    // Combinada: usar 🎯 si MULTI (multi-deporte) o icono del deporte top.
    const ligaUpper = (signal.liga || '').toUpperCase();
    headerIcon = ligaUpper === 'MULTI' ? '🎯' : icon;
    headerTitle = 'COMBINADA';
  } else if (isBetBuilder) {
    headerTitle = 'BET BUILDER';
  } else {
    headerTitle = signal.partido || '—';
  }

  // Liga visible en metadata: omite si es null/vacía o si es "MULTI" (ya
  // reflejado en el header con 🎯 + nombre "COMBINADA").
  const ligaMetaVisible = signal.liga && (signal.liga.toUpperCase() !== 'MULTI');

  return (
    <div style={{ ...cardStyle, opacity: cardOpacity }}>
      {/* Fila 1: icono + título + chips */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 10,
          marginBottom: 8,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: partidoColor,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <span style={{ fontSize: 16, lineHeight: 1, flexShrink: 0 }} aria-hidden="true">
              {headerIcon}
            </span>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {headerTitle}
            </span>
          </div>
          <div
            style={{
              fontSize: 11,
              color: metaColor,
              marginTop: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              flexWrap: 'wrap',
            }}
          >
            {ligaMetaVisible && <span>{signal.liga}</span>}
            {(isCombinada || isBetBuilder) && (
              <span style={{ fontWeight: 600 }}>
                {selecciones.length} {selecciones.length === 1 ? 'selección' : 'selecciones'}
              </span>
            )}
            <span
              title="Pick enviado manualmente durante pausa técnica"
              style={{
                background: '#FF6B2C',
                color: 'white',
                fontSize: 9.5,
                fontWeight: 700,
                padding: '1px 6px',
                borderRadius: 4,
                letterSpacing: 0.3,
                whiteSpace: 'nowrap',
              }}
            >
              📍 PICK MANUAL
            </span>
            {/* Mercado en chip: solo para simple. Combinada/betbuilder lo muestran
                en la lista de selecciones más abajo. */}
            {!isCombinada && !isBetBuilder && signal.mercado && (
              <span
                style={{
                  background: isWon ? 'white' : 'var(--bg-subtle)',
                  color: isWon ? '#04342C' : 'var(--ink-2)',
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '2px 8px',
                  borderRadius: 6,
                  border: isWon ? '0.5px solid #5DCAA5' : 'none',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                }}
              >
                {signal.mercado}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
          {isWon && (
            <span
              style={{
                background: '#1D9E75',
                color: 'white',
                fontSize: 11,
                fontWeight: 700,
                padding: '4px 11px',
                borderRadius: 999,
                whiteSpace: 'nowrap',
                letterSpacing: '0.04em',
              }}
            >
              {isCashoutWin ? '💰 CASHOUT' : 'GANÓ ✓'}
            </span>
          )}
          {isCashoutLoss && (
            <span
              style={{
                background: 'var(--bg-subtle)',
                color: 'var(--muted)',
                fontSize: 10,
                fontWeight: 600,
                padding: '3px 9px',
                borderRadius: 999,
                whiteSpace: 'nowrap',
              }}
            >
              💸 CASHOUT
            </span>
          )}
          {isPush && (
            <span
              style={{
                background: 'var(--bg-subtle)',
                color: 'var(--ink-2)',
                fontSize: 10,
                fontWeight: 600,
                padding: '3px 9px',
                borderRadius: 999,
                whiteSpace: 'nowrap',
              }}
            >
              PUSH
            </span>
          )}
          {isPending && (
            <span
              style={{
                background: 'var(--info-soft, #E3F2FD)',
                color: 'var(--info, #1976D2)',
                fontSize: 10,
                fontWeight: 600,
                padding: '3px 9px',
                borderRadius: 999,
                whiteSpace: 'nowrap',
              }}
            >
              EN CURSO
            </span>
          )}
        </div>
      </div>

      {/* Sub-bloque (solo combinada/betbuilder): partido común del betbuilder
          + lista de selecciones. Compacto, vertical, legible en mobile. */}
      {(isCombinada || isBetBuilder) && selecciones.length > 0 && (
        <div
          style={{
            marginBottom: 8,
            paddingTop: 4,
            paddingBottom: 4,
            paddingLeft: 8,
            borderLeft: `2px solid ${isWon ? '#0F6E56' : 'var(--border)'}`,
            fontSize: 12,
            lineHeight: 1.45,
            color: partidoColor,
          }}
        >
          {/* Para betbuilder: mostramos el partido común una sola vez arriba */}
          {isBetBuilder && signal.partido && (
            <div style={{ fontWeight: 600, marginBottom: 4, color: partidoColor }}>
              {signal.partido}
            </div>
          )}
          {selecciones.map((s, idx) => {
            const sIcon = isBetBuilder
              ? '▪️'
              : DEPORTE_ICONS[(s.deporte || 'otro').toLowerCase()] || DEPORTE_ICONS.otro;
            const sCuota = Number.isFinite(s?.cuota)
              ? ` @ ${Number(s.cuota).toFixed(2)}`
              : '';
            // Combinada: partido + mercado + cuota_indiv
            // BetBuilder: solo mercado (mismo partido para todos)
            const sText = isBetBuilder
              ? `${s.mercado || '—'}`
              : `${s.partido || '—'} — ${s.mercado || '—'}${sCuota}`;
            return (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 6,
                  marginBottom: idx < selecciones.length - 1 ? 3 : 0,
                  wordBreak: 'break-word',
                }}
              >
                <span style={{ flexShrink: 0, lineHeight: '1.45' }} aria-hidden="true">
                  {sIcon}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>{sText}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Fila 2: datos cuota / stake / yield / hora */}
      <div
        className="signals-card-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
          gap: 6,
          paddingTop: 8,
          borderTop: `0.5px solid ${isWon ? '#9FE1CB' : 'var(--border)'}`,
        }}
      >
        <DataBlock
          label={(isCombinada || isBetBuilder) ? 'CUOTA TOTAL' : 'CUOTA'}
          value={Number.isFinite(odds) ? odds.toFixed(2) : '—'}
          color={isWon ? '#04342C' : undefined}
          mono
          bold
        />
        <DataBlock
          label="STAKE"
          value={Number.isFinite(stake) ? `${stake}u` : '—'}
          color={isWon ? '#04342C' : undefined}
          mono
        />
        <DataBlock
          label="YIELD"
          value={
            yu == null
              ? '—'
              : `${yu >= 0 ? '+' : ''}${Number(yu).toFixed(2)}u`
          }
          color={
            yu == null
              ? 'var(--muted)'
              : yu > 0
              ? (isWon ? '#0F6E56' : 'var(--pos)')
              : yu < 0
              ? 'var(--neg)'
              : 'var(--ink-2)'
          }
          bold
          mono
        />
        <DataBlock
          label="ENVIADO"
          value={startedAt ? formatTime(startedAt) : '—'}
          color={isWon ? '#04342C' : 'var(--ink-2)'}
          mono
        />
      </div>
    </div>
  );
}

function DataBlock({ label, value, color, bold, mono }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{
        fontSize: 9,
        color: 'var(--muted)',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        marginBottom: 2,
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 13,
        fontWeight: bold ? 700 : 500,
        color: color || 'var(--ink)',
        fontFamily: mono ? 'ui-monospace, monospace' : 'inherit',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {value}
      </div>
    </div>
  );
}
